// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { FolderOpen, Mail, ArrowLeft, CheckCircle, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setEmailSent(true);
      toast.success('Email enviado! Verifique sua caixa de entrada.');
    } catch (error) {
      toast.error('Erro ao enviar email. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white p-4">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3">
          <img 
            src="/logo.png" 
            alt="JW Donate Audit Logo" 
            className="w-14 h-14 object-contain"
          />
          <span className="font-extrabold text-2xl tracking-tight text-slate-900 font-heading">
            JW Donate Audit
          </span>
        </div>

        <Card className="border-0 shadow-xl rounded-2xl overflow-hidden">
          <CardHeader className="text-center pb-2 pt-8">
            <CardTitle className="text-2xl font-bold font-heading">
              {emailSent ? 'Email Enviado!' : 'Recuperar Senha'}
            </CardTitle>
            <CardDescription className="text-base">
              {emailSent 
                ? 'Verifique sua caixa de entrada e siga as instruções'
                : 'Digite seu email para receber um link de recuperação'
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="p-8 pt-6">
            {emailSent ? (
              <div className="text-center space-y-6">
                <div className="w-20 h-20 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto">
                  <CheckCircle className="w-10 h-10 text-emerald-600" />
                </div>
                <div className="space-y-3">
                  <p className="text-slate-700 leading-relaxed">
                    Um email foi enviado para <strong className="text-slate-900">{email}</strong> com instruções para criar uma nova senha.
                  </p>
                  <p className="text-sm text-slate-500">
                    O link expira em 1 hora. Se não receber, verifique a pasta de spam.
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setEmailSent(false)}
                  className="w-full h-12 rounded-xl font-medium"
                >
                  Enviar novamente
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
                  <div className="relative group">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-slate-600 transition-colors" />
                    <Input
                      id="email"
                      data-testid="forgot-email-input"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-11 h-12 rounded-xl border-slate-200 bg-white focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-all"
                      required
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  data-testid="forgot-submit-button"
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 rounded-xl font-semibold shadow-lg shadow-slate-900/20 hover:shadow-xl transition-all duration-200 active:scale-[0.98]"
                  disabled={isLoading}
                >
                  {isLoading ? 'Enviando...' : (
                    <>
                      Enviar Link de Recuperação
                      <Send className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            )}

            <div className="mt-8 text-center">
              <Link 
                to="/" 
                className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900 font-medium transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                Voltar para o login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
